import { Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Store } from '../../../store';
import { CallRecordingsSearchParams, defaultRecordingsLogsSearchParams } from '../../../data-types/search-params';
import { AlertConfirmable } from 'src/app/decorators/alert-confirmable.decorator';
import { TwilioService } from '../../../services/twilio.service';
import { AlertService } from '../../../services/alert.service';
import { bufferCount, delay, filter, first, last, takeUntil, withLatestFrom } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UserService } from '../../../services/user.service';
import { UserAccountType } from '../../../data-types/user-data';
import { FormBuilder } from '@angular/forms';
import { NotesService } from '../../../services/notes.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { ActionService } from '../../../services/action.service';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { debounce } from 'lodash';
import { Socket } from 'ngx-socket-io';
import { environment as env } from '../../../../environments/environment';
import { CustomAlertData, DefaultCustomAlertData } from '../../../data-types/alert-data';
import { Notes } from '../../../models/notes';
import { ToolsService } from '../../../services/tools.service';

@Component({
    selector: 'app-calls-recordings',
    templateUrl: './calls-recordings.component.html',
    styleUrls: ['./calls-recordings.component.scss'],
    animations: [
        trigger('fadeIn', [
            state('void', style({ opacity: '0' })),
            transition('void <=> *', [animate('150ms ease-in')]),
        ]),
        trigger('fadeOut', [
            state('*', style({ opacity: '1' })),
            transition('* <=> void', [animate('100ms ease-out')]),
        ]),
        trigger('fadeInOut', [
            state('void', style({ opacity: '0' })),
            transition('void => *', [animate('150ms ease-out')]),
            state('*', style({ opacity: '1' })),
            transition('* => void', [animate('50ms ease-in')]),
        ]),
    ],
})
export class CallsRecordingsComponent implements OnInit, OnDestroy {
    @Input() recordingSource: 'RecordVerb' | undefined = undefined;
    isLoading = false;
    env = env;

    bsConfig?: Partial<BsDatepickerConfig> = Object.assign(
        {},
        {
            containerClass: 'theme-default test',
            isAnimated: true,
            dateInputFormat: 'DD/MM/YYYY',
            keepDatepickerOpened: true,
        }
    );

    logSwitchItems = [
        { switchName: 'Mine', isSwitchActive: true, id: 1 },
        { switchName: 'All', isSwitchActive: false, id: 2 },
    ];

    accountTypes: Array<{ name: string; value: string }> = [];

    @ViewChild('dropDownSelect') dropDownSelect: ElementRef<HTMLElement>;
    @ViewChild('notesTmpl', { read: TemplateRef }) notesTmpl: TemplateRef<any>;

    callRecordings: Array<any> = [];
    recordingParams: CallRecordingsSearchParams = {
        ...defaultRecordingsLogsSearchParams,
    };

    sound = {};
    playing = null;
    audio = null;
    callNotes: Notes[] | null = null;

    destroy$ = new Subject<void>();

    currentPhone;
    user = this.store.selectForLocal('user');

    callRecordingsListDebounce;
    private callRecordingsListDebounce$ = new Subject<Subscription>();
    private voiceSocket: Socket; // here comes all sorts of messages from CRM

    constructor(
        private store: Store,
        private notesService: NotesService,
        private actionService: ActionService,
        private userService: UserService,
        public twilioService: TwilioService,
        private alertService: AlertService,
        private fb: FormBuilder,
        private activatedRouteSnapshot: ActivatedRoute,
        private router: Router,
        public toolsService: ToolsService,
    ) {

        this.recordingParams.phoneNr = this.twilioService.numbersForDomain.map(item => item.phone);
        this.currentPhone = this.twilioService.numbersForDomain;
        this.callRecordingsList();

        this.accountTypes = Object.keys(UserAccountType).map((key) => ({
            value: UserAccountType[key],
            name: key,
        }));
        // http://localhost:4200/accounts/calls;newCustomerMob=5555454
        this.activatedRouteSnapshot.params
            .pipe(
                filter(
                    (data) =>
                        data.hasOwnProperty('newCustomerMob') ||
                        data.hasOwnProperty('newCustomerNote') ||
                        data.hasOwnProperty('newCustomerAction')
                ),
                delay(100)
            )
            .subscribe(({ newCustomerMob, newCustomerNote, newCustomerAction }) => {
                this.router.navigate(router.url.split(';')[0].split('/'), {
                    replaceUrl: true,
                });
            });
        this.callRecordingsListDebounce = debounce(this.callRecordingsList, 350);
        this.callRecordingsListDebounce$.pipe(bufferCount(2, 1)).subscribe(([oldSub, newSub]) => oldSub.unsubscribe());
    }

    ngOnInit(): void {
        this.recordingParams.source = this.recordingSource;
        this.callRecordingsList();
        if (this.voiceSocket) {
            this.voiceSocket.disconnect();
        }
        this.voiceSocket = new Socket({ url: `${env.socketPath}voice`, options: { withCredentials: true } });
        this.voiceSocket.fromEvent('log-row').subscribe((logRow: { callSid: string }) => this.callRecordingsList());
    }

    callRecordingsList(param?: { [property in keyof CallRecordingsSearchParams]?: CallRecordingsSearchParams[property]; }) {
        this.isLoading = true;

        if (param) {
            this.recordingParams = { ...this.recordingParams, page: 0, ...param } as CallRecordingsSearchParams;
        }

        this.callRecordingsListDebounce$.next(
            this.twilioService
                .getCallRecordingsForNr(
                    this.recordingParams.page * this.recordingParams.limit,
                    this.recordingParams.limit,
                    {
                        dir: this.recordingParams.direction,
                        accType: this.recordingParams.accountType,
                        searchWord: this.recordingParams.name,
                        date: this.recordingParams.date,
                        sort: this.recordingParams.sort,
                        ascOrDesc: this.recordingParams.ascOrDesc,
                        source: this.recordingParams.source,
                    },
                    this.recordingParams.phoneNr
                )
                .pipe(takeUntil(this.destroy$), first())
                .subscribe((result: { list: Array<any>; totalRecords: number }) => {
                    this.callRecordings = [];
                    if (result.list.length > 0) {
                        this.callRecordings = result.list;
                    }

                    if (result.totalRecords) {
                        this.recordingParams.pages = Math.ceil(result.totalRecords / this.recordingParams.limit);
                        this.recordingParams.totalRecords = result.totalRecords;
                        this.recordingParams.pageArray = [];
                        for (let i = 0; i < this.recordingParams.pages; i++) {
                            this.recordingParams.pageArray.push(i);
                        }
                    }

                    this.isLoading = false;
                })
        );
    }

    @AlertConfirmable({ title: 'Do you want to make a call?' })
    makeACallLog(callObjet: any) {
        if (!this.twilioService.isLineBusy) {
            this.twilioService.startOutboundCall(callObjet.direction === 'out' ? callObjet.callTo : callObjet.callFrom);
        } else {
            this.alertService.error([
                'Line is busy, please nicely finish ongoing conversation before starting the new one.',
            ]);
        }
    }

    initPlaybackRecord(callRecord: any) {
        if (this.audio) {
            this.audio.pause();
        } else {
            this.audio = document.createElement('audio');
            this.audio.setAttribute('preload', 'auto');
            this.audio.setAttribute('controls', 'none');
            this.audio.style.display = 'none';
            document.body.appendChild(this.audio);
        }

        this.audio.src = callRecord.mediaFile + '.mp3';
        this.audio.play();
        this.playing = callRecord.sId;
    }

    ngOnDestroy(): void {
        if (this.audio) {
            this.audio.pause();
            document.removeChild(this.audio);
            this.audio = null;
        }
        this.destroy$.next();
        if (this.voiceSocket) {
            this.voiceSocket.disconnect();
        }
    }

    stopPlayback(reSid: string) {
        this.audio.pause();
        this.playing = null;
    }

    showNotesFor(callLog) {
        this.callNotes = null;
        this.twilioService.getNotes(callLog.sId).subscribe(notes => this.callNotes = notes);
        this.alertService
            .customAlert({
                    ...DefaultCustomAlertData,
                    template: this.notesTmpl,
                    cancelText: null,
                    confirmText: 'Close'
                } as CustomAlertData
            );
    }

    set accountType(accType: UserAccountType | '') {}

    get UserAccountType(): typeof UserAccountType {
        return UserAccountType;
    }

    get UserAccountTypeEnum() {
        return Object.values(UserAccountType);
    }
}
